import { errorLogger } from "@/Common/helpers/logger";
import { apiInstance, gql } from "@/services/httpAdapter";
import { GraphqlQueryResponse } from "@/Common/api/types/GraphqlQueryResponse";
import { ISalesDepartmentalWashout } from "@/Washouts/types/Api/SalesDepartmental";

// Common fragments
import {
	AddedCompensationFragment,
	CallActivityPerformanceFragment,
	ChargebacksFragment,
	CompensationFragment,
	EmployeeFragment,
	LongevityFragment,
	ReviewFragment,
} from "@/Washouts/api/common/fragments";

// Fragments
import { DepartmentalWashoutTableFragment } from "@Washouts/api/fragments/departmentalManagers";
import { WashoutCrossStoreSalesFragment, WashoutNewCarsSalesFragment } from "@Washouts/api/Departments/managers/sales/fragments";

type Params = string[];

export async function getSalesDepartmentalWashout(...params: Params) {
	const [locationId, userId, commissionCycleId, payPeriodId, position] = params;
	const query = gql`
		query getSalesDepartmentalWashout(
			$locationId: String!
			$userId: String!
			$commissionCycleId: String!
			$payPeriodId: String!
			$position: String!
		) {
			getSalesDepartmentalWashout(
				locationId: $locationId
				userId: $userId
				commissionCycleId: $commissionCycleId
				payPeriodId: $payPeriodId
				position: $position
			) {
				_id
				userId
				commissionCycleId
				payPeriodId
				created
				employee {
					...Employee
				}
				longevity {
					...Longevity
				}
				newChargebacks {
					...Chargebacks
				}
				preOwnedChargebacks {
					...Chargebacks
				}
				deductions {
					deductions {
						_id
						title
						value
					}
					total
				}
				spiffs {
					spiffs {
						_id
						date
						spiffName
						spiffType
						amount
						stocksNumbers
					}
					total
				}
				review {
					...Review
				}
				callActivityPerformance {
					...WashoutCallActivityPerformance
				}
				newCarsSales {
					...NewCarsSales
				}
				preOwnedCarsSales {
					...DepartmentalWashoutTable
				}
				financeGross {
					...DepartmentalWashoutTable
				}
				variableOperationsCosts {
					...DepartmentalWashoutTable
				}
				crossStoreSales {
					...WashoutCrossStoreSales
				}
				addedCompensations {
					...AddedCompensation
				}
				summary {
					...Compensation
				}
			}
		}
		${DepartmentalWashoutTableFragment}
		${AddedCompensationFragment}
		${CallActivityPerformanceFragment}
		${ChargebacksFragment}
		${CompensationFragment}
		${EmployeeFragment}
		${LongevityFragment}
		${ReviewFragment}
		${WashoutNewCarsSalesFragment}
		${WashoutCrossStoreSalesFragment}
	`;

	const variables = {
		locationId,
		commissionCycleId,
		userId,
		payPeriodId,
		position,
	};

	try {
		const response: GraphqlQueryResponse<"getSalesDepartmentalWashout", ISalesDepartmentalWashout> = await apiInstance.post(
			"/graphql",
			{
				query,
				variables,
			}
		);
		return response.data.data?.getSalesDepartmentalWashout;
	} catch (error) {
		errorLogger(error as Error);
		return null;
	}
}

export async function getSalesDepartmentalWashoutReview(...params: Params) {
	const [locationId, commissionCycle, payPeriodId = null, position] = params;
	const query = gql`
		query getSalesDepartmentalWashout($locationId: String!, $commissionCycleId: String!, $payPeriodId: String, $position: String) {
			getSalesDepartmentalWashout(
				commissionCycleId: $commissionCycleId
				locationId: $locationId
				payPeriodId: $payPeriodId
				position: $position
			) {
				_id
				review {
					...Review
				}
			}
		}
		${ReviewFragment}
	`;

	const variables = {
		locationId,
		payPeriodId,
		position,
		commissionCycleId: commissionCycle,
	};

	try {
		const response: GraphqlQueryResponse<
			"getSalesDepartmentalWashout",
			Pick<ISalesDepartmentalWashout, "_id" | "review">
		> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getSalesDepartmentalWashout;
	} catch (error) {
		errorLogger(error as Error);
		return null;
	}
}
